/**
 * A widget type's `config_schema`: what shape it must have, what it says about each
 * property, and how it becomes something that validates a config.
 *
 * `.plans/ai-product-placer/README.md` §4.1 draws the boundary this file sits on:
 * "The portal validates shape and reads only the schema's annotations, never a config
 * value." A widget type carries a JSON Schema; a widget snapshots it at creation; the
 * studio sets a config that is validated against the snapshot; the client's page draws
 * a table from the same snapshot's titles. Nothing here knows what `origins` means,
 * and the service that does applies **its own** zod to what it pulls — two validators
 * on purpose (`.plans/ai-product-placer/plan/00-plan.md` §5, accepted repetition 2).
 *
 * **It lives in `packages/shared` because two modules need it.** `catalog/service.ts`
 * checks the shape when a type is written and `usage/service.ts` validates a config
 * against a widget's snapshot; `usage/service.ts` already imports catalog's queries,
 * so either module owning this would be an import cycle
 * (`evidence/plan-review.md` B1).
 *
 * **The schema is flat, and that is a decision rather than a simplification.** The
 * studio's form draws a config property with `fieldOf`, which renders a nested object
 * only when it is exactly `{ amount, currency }` and otherwise nulls the whole form
 * (`evidence/portal.md` §D.4). A flat schema of scalars, enums and string arrays is
 * what a form can draw and what a `Kv` can print, and the bound (24 properties, 8 KB)
 * is what keeps a thing pulled every minute and rendered on a page from growing into
 * a document.
 */
import { z } from 'zod';
import { DomainError } from '../errors.js';
/**
 * At most twenty-four properties in a config schema.
 *
 * The panel prints one `Kv` row per property and the studio's form draws one field
 * per property; both stop being readable long before this. It is a bound rather than
 * a target — the placer's own schema has eight.
 */
export const CONFIG_MAX_PROPERTIES = 24;
/**
 * At most 8 KB of stored config, measured as the UTF-8 JSON.
 *
 * The same number as `INGEST_BODY_LIMIT_BYTES`, and for a related reason rather than
 * the same one: that bounds what a widget may **post**, and this bounds what it may
 * **pull** every minute over the same door. The measurement is the API's, because
 * this package compiles without Node types and without the DOM and so has no
 * `TextEncoder` — `usage/service.ts` measures it with `Buffer.byteLength`, exactly as
 * `usage/routes.ts` measures `meta`.
 */
export const CONFIG_MAX_BYTES = 8 * 1024;
/**
 * Who may change a property.
 *
 * `x-editor: studio` is the studio's alone; `x-editor: client` is one an owner or an
 * admin may edit from their own portal (README §3.3, v1.1). In v1 the annotation is
 * carried and read by nobody but this file — it is here now because the schema is
 * seeded now, and adding it later would mean re-writing every widget's snapshot.
 */
export const CONFIG_EDITORS = ['studio', 'client'];
/**
 * The properties of a config schema, in schema order, or a refusal saying which one
 * is wrong.
 *
 * One reader, used three ways: `assertConfigSchemaShape` is this function's refusals,
 * the client's panel is this function's answers, and P5's editor is this function
 * filtered to `editor === 'client'`. A second reader would be a second opinion about
 * what a config schema is.
 */
export function configFieldsOf(schema) {
    const root = asObject(schema, 'A config schema is a JSON Schema object.', {});
    if (root['type'] !== 'object') {
        throw refusal('A config schema has "type": "object".', { type: label(root['type']) });
    }
    if (root['additionalProperties'] !== false) {
        throw refusal('A config schema sets "additionalProperties": false, so a key nobody declared is ' +
            'refused rather than stored.', { additionalProperties: label(root['additionalProperties']) });
    }
    const properties = asObject(root['properties'], 'A config schema has "properties".', {});
    const keys = Object.keys(properties);
    if (keys.length === 0) {
        throw refusal('A config schema with no properties is a type that takes no config: use null.', {});
    }
    if (keys.length > CONFIG_MAX_PROPERTIES) {
        throw refusal(`A config schema holds at most ${String(CONFIG_MAX_PROPERTIES)} properties; the client's ` +
            'page prints one row for each of them.', { properties: keys.length, limit: CONFIG_MAX_PROPERTIES });
    }
    const required = requiredOf(root, keys);
    return keys.map((key) => fieldOf(key, properties[key], required.has(key)));
}
/**
 * The same properties, or **null** for a schema this system cannot read.
 *
 * For a reader that cannot afford a refusal. `configFieldsOf` throws, which is right
 * at a write — the studio is told which property is wrong and the row is not stored
 * — and wrong in a React render, where one bad `config_schema` would take a client's
 * whole widget page down with it. Nothing should be able to write such a row
 * (`assertConfigSchemaShape` runs on both catalog actions and `seed/catalog.test.ts`
 * walks the seeds), so this is the second layer rather than the first.
 */
export function configFieldsOrNull(schema) {
    try {
        return configFieldsOf(schema);
    }
    catch {
        return null;
    }
}
/**
 * Refuses a `config_schema` that this system cannot carry.
 *
 * Called at write time by `catalog_create_widget_type!` and
 * `catalog_update_widget_type!`, so a schema that a page could not draw never reaches
 * a widget's snapshot. `null` is legal and means "this type takes no config" — the
 * two widget types that existed before the placer stay exactly that.
 */
export function assertConfigSchemaShape(schema) {
    configFieldsOf(schema);
    // Convertibility is part of the shape: a schema the portal cannot turn into a
    // validator is one that would be refused later, at `widgets_set_config`, by which
    // point the type is on the card.
    configSchemaToZod(schema);
}
/**
 * The schema as something that validates a config.
 *
 * `z.fromJSONSchema` and nothing else. It tolerates `title` and `x-editor` — unknown
 * keywords are carried past rather than refused — and it **applies `default`**, so a
 * config stored through it holds every key the schema declares whether or not the
 * caller sent one. That is what makes the client's panel a table of facts rather
 * than a table with holes in it, and it is pinned by a test in
 * `widgetConfig.test.ts` because it is a property of zod rather than of this file.
 */
export function configSchemaToZod(schema) {
    try {
        return z.fromJSONSchema(schema);
    }
    catch (error) {
        throw refusal('That config schema is not one this system can read.', {
            reason: error instanceof Error ? error.message : String(error),
        });
    }
}
/**
 * A config validated against a schema, or a refusal naming the key that is wrong.
 *
 * The JSON path is in the problem detail rather than only in the message, because
 * the caller is as often an agent over MCP as a person: "config.origins" is
 * something to act on and "invalid input" is not.
 */
export function parseConfig(schema, config) {
    const parsed = configSchemaToZod(schema).safeParse(config);
    if (!parsed.success) {
        const [issue] = parsed.error.issues;
        throw refusal(issue === undefined ? 'That config does not match the schema.' : issue.message, {
            path: pathOf(issue?.path ?? []),
            ...(issue === undefined ? {} : { code: issue.code }),
        });
    }
    return parsed.data;
}
/* ------------------------------------------------------------------ the reader */
function fieldOf(key, node, required) {
    const property = asObject(node, `Property "${key}" is not an object.`, { property: key });
    const title = property['title'];
    if (typeof title !== 'string' || title.length === 0) {
        throw refusal(`Property "${key}" needs a "title": it is the label the client reads.`, {
            property: key,
        });
    }
    const editor = property['x-editor'];
    if (typeof editor !== 'string' || !isEditor(editor)) {
        throw refusal(`Property "${key}" needs "x-editor": "studio" or "client" — who may change it.`, {
            property: key,
            'x-editor': label(editor),
        });
    }
    return { key, title, type: typeOf(key, property), editor, required };
}
function typeOf(key, property) {
    const type = property['type'];
    if (type === 'boolean')
        return 'boolean';
    if (type === 'integer')
        return 'integer';
    if (type === 'string') {
        const values = property['enum'];
        if (values === undefined)
            return 'string';
        if (!Array.isArray(values) || values.length === 0 || !values.every(isString)) {
            throw refusal(`Property "${key}"'s "enum" is a non-empty list of strings.`, { property: key });
        }
        return 'enum';
    }
    if (type === 'array') {
        const items = asObject(property['items'], `Property "${key}" needs "items".`, { property: key });
        if (items['type'] !== 'string') {
            throw refusal(`Property "${key}" is an array of strings or it is not an array.`, {
                property: key,
                items: label(items['type']),
            });
        }
        if (typeof property['maxItems'] !== 'number') {
            throw refusal(`Property "${key}" needs "maxItems": a list pulled every minute and rendered on a ` +
                'page has an end.', { property: key });
        }
        return 'string_array';
    }
    if (type === 'object') {
        throw refusal(`Property "${key}" is a nested object, and a config schema is flat: scalars, enums and ` +
            'string arrays. A nested object is a field the studio’s form cannot draw.', { property: key });
    }
    throw refusal(`Property "${key}" has a type this system does not carry. A config property is a string, ` +
        'an integer, a boolean, a string enum or an array of strings.', { property: key, type: label(type) });
}
function requiredOf(root, keys) {
    const declared = root['required'];
    if (declared === undefined)
        return new Set();
    if (!Array.isArray(declared) || !declared.every(isString)) {
        throw refusal('A config schema’s "required" is a list of property names.', {});
    }
    for (const name of declared) {
        if (!keys.includes(name)) {
            throw refusal(`"required" names "${name}", which is not a property of this schema.`, {
                property: name,
            });
        }
    }
    return new Set(declared);
}
/* ----------------------------------------------------------------- the plumbing */
function asObject(value, message, meta) {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
        throw refusal(message, meta);
    }
    return value;
}
function isEditor(value) {
    return CONFIG_EDITORS.includes(value);
}
function isString(value) {
    return typeof value === 'string';
}
/** `["origins", 0]` as `origins.0`, and an empty path as the root. */
function pathOf(path) {
    return path.length === 0 ? '(root)' : path.map((part) => String(part)).join('.');
}
function refusal(message, meta) {
    return new DomainError('ValidationFailed', message, meta);
}
/** An unknown value as something a problem body may print. */
function label(value) {
    if (typeof value === 'string')
        return value;
    if (value === undefined)
        return 'undefined';
    return JSON.stringify(value);
}
//# sourceMappingURL=widgetConfig.js.map